<template>
  <v-container class="pa-0" fluid>
    <v-row class="ma-0 ma-md-2">
      <v-col cols="12">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>
              <v-avatar height="32px" class="mx-auto">
                <videon-logo />
              </v-avatar>
              API Usage
            </v-toolbar-title>
          </v-toolbar>
          <v-divider/>
          <v-card-text>
            <v-alert dense outlined type="error" v-if="error">{{ errorText }}</v-alert>
            <v-row v-if="!loading && !entitled" class="text-center">
              <v-col cols="12">
                <span class="text--disabled">Cloud Pro API is not licensed for this organization.</span>
              </v-col>
            </v-row>
            <div v-else-if="usage">
              <p class="caption mb-2">
                The tiles below cover the current quota period:
                {{ formatUsageTime(usage.period_start) }} –
                {{ formatUsageTime(usage.reset_at) }}. They always show
                organization-wide quota. Everything under the period selector
                reflects the explorer range and filters you choose.
              </p>
              <v-row>
                <v-col cols="12" md="3">
                  <videon-metric-tile heading="Used">{{ usage.used }}</videon-metric-tile>
                </v-col>
                <v-col cols="12" md="3">
                  <videon-metric-tile heading="Remaining">{{ usage.remaining }}</videon-metric-tile>
                </v-col>
                <v-col cols="12" md="3">
                  <videon-metric-tile heading="Allowance">{{ usage.allowance }}</videon-metric-tile>
                </v-col>
                <v-col cols="12" md="3">
                  <videon-metric-tile heading="Percent">{{ usage.percent }}%</videon-metric-tile>
                </v-col>
              </v-row>
              <v-progress-linear
                :value="usage.percent"
                :color="barColor"
                height="12"
                class="mb-4"
              />
              <p>
                Status: <strong>{{ usage.threshold_status }}</strong>.
                Resets {{ formatUsageTime(usage.reset_at) }}.
              </p>
              <v-alert dense outlined type="warning" v-if="usage.threshold_status === 'warning' || usage.threshold_status === 'critical'">
                This organization is approaching its Cloud Pro API quota.
                Request a quota add-on through the existing sales-order process.
              </v-alert>
              <v-alert dense outlined type="error" v-if="usage.threshold_status === 'limit'">
                The current quota is exhausted. Additional PAT calls receive HTTP 429
                when enforcement is enabled. Request a quota add-on through sales order.
              </v-alert>
              <h3 class="mt-6 mb-2">API Usage Explorer</h3>
              <v-row class="mx-0">
                <v-col cols="12" md="4">
                  <v-select
                    v-model="selectedTokens"
                    :items="tokenChoices"
                    item-text="text"
                    item-value="value"
                    label="Filter tokens"
                    hint="OR within tokens; AND with endpoints"
                    persistent-hint
                    multiple
                    chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="selectedEndpoints"
                    :items="endpointChoices"
                    label="Filter endpoints"
                    hint="OR within endpoints; AND with tokens"
                    persistent-hint
                    multiple
                    chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-checkbox
                    v-model="showSummed"
                    :disabled="!explorerFiltered"
                    hide-details
                    dense
                    label="Show summed result"
                  />
                </v-col>
              </v-row>
              <p v-if="filterCaption" class="caption mb-2">{{ filterCaption }}</p>
              <v-alert
                dense
                outlined
                type="warning"
                v-if="filterDegradedWarning"
                class="mb-2"
              >
                {{ filterDegradedWarning }}
              </v-alert>
              <v-alert
                dense
                outlined
                type="warning"
                v-if="selectionTruncated"
                class="mb-2"
              >
                Only the first five tokens and five endpoints are sent to the API.
                Narrow the selection to inspect a specific combination.
              </v-alert>
              <v-alert
                dense
                outlined
                type="warning"
                v-if="correlationWarning"
                class="mb-2"
              >
                {{ correlationWarning }}
              </v-alert>
              <period-selection
                v-model="period"
                :initialRange="periodKey"
                :initialStart="periodStart"
                :initialEnd="periodEnd"
                :maxCustomDays="31"
                allowMonthlyUsage
                allowQuotaPeriod
                :quotaPeriodBounds="currentQuotaPeriodBounds"
              />

              <v-alert
                dense
                outlined
                type="info"
                v-if="quotaPeriodRows.length > 1"
                class="mt-3 mb-2"
              >
                The selected explorer range crosses a quota reset. Explorer totals
                include calls from every period listed below; the Used tile includes
                only enforce-mode calls reserved in the current quota period.
              </v-alert>
              <v-data-table
                v-if="quotaPeriodRows.length > 1"
                :headers="quotaPeriodHeaders"
                :items="quotaPeriodRows"
                :hide-default-footer="true"
                :items-per-page="100"
                dense
                class="mb-3"
              />

              <v-alert dense outlined type="error" v-if="chartError" class="mt-3">{{ chartErrorText }}</v-alert>
              <v-progress-linear v-if="chartLoading" color="primary" indeterminate class="mt-3" />
              <v-tabs v-model="section" color="secondary" class="mt-3">
                <v-tab href="#graph">Graph</v-tab>
                <v-tab href="#data">Data</v-tab>
              </v-tabs>
              <v-divider />
              <v-tabs-items v-model="section">
                <v-tab-item value="graph">
                  <videon-chart
                    v-if="activity"
                    heading="PAT requests"
                    unit="requests"
                    :deviceMetrics="activity"
                    :colorByDevice="chartColorBySelection"
                    :pointStyles="pointStyles"
                    :pointStyleInLegend="true"
                    :pointStyleInTooltip="true"
                    :showLegend="true"
                    :startTime="period && period.start"
                    :endTime="period && period.end"
                  />
                </v-tab-item>
                <v-tab-item value="data">
                  <v-card flat>
                    <v-card-title>
                      Request activity
                      <v-spacer />
                      <v-btn color="secondary" small outlined @click="exportAsCsv">Download CSV</v-btn>
                    </v-card-title>
                    <v-data-table
                      :headers="tableHeaders"
                      :items="tableData"
                      :hide-default-footer="true"
                      :items-per-page="10000"
                      dense
                    />
                  </v-card>
                </v-tab-item>
              </v-tabs-items>

              <h3 class="mt-6 mb-2">Daily trend {{ rangeSuffix }}</h3>
              <p v-if="quotaPeriodRows.length > 1" class="caption mb-2">
                UTC daily rows can straddle a quota reset. Use the quota-period
                breakdown above to reconcile explorer calls with the Used tile.
              </p>
              <v-data-table
                :headers="[{text:'Day', value:'key'}, {text:'Calls', value:'used'}]"
                :items="dailyInRange"
                hide-default-footer
                dense
              />
              <h3 class="mt-6 mb-2">By token {{ rangeSuffix }}</h3>
              <p class="caption mb-2">
                Prefix and comment identify the PAT without showing the secret.
                Username and email identify the account that created the token.
                <template v-if="explorerFiltered && !selectedTokens.length">
                  Token totals are scoped to the selected endpoint(s) when correlated
                  PAT × endpoint history exists for this range.
                </template>
                <template v-else-if="!explorerFiltered">
                  Every PAT with traffic in this range is listed even though the
                  chart shows only the organization total.
                </template>
              </p>
              <v-data-table
                :headers="tokenHeaders"
                :items="tokensInRange"
                hide-default-footer
                dense
              />
              <h3 class="mt-6 mb-2">By endpoint {{ rangeSuffix }}</h3>
              <p v-if="explorerFiltered && !selectedEndpoints.length" class="caption mb-2">
                Endpoint totals are scoped to the selected token(s) when correlated
                PAT × endpoint history exists for this range.
              </p>
              <v-data-table
                :headers="[{text:'Endpoint', value:'key'}, {text:'Calls', value:'used'}]"
                :items="endpointsInRange"
                hide-default-footer
                dense
              />
              <p v-if="activity && activity.truncated" class="caption text--secondary mt-2 mb-0">
                Only the busiest tokens and endpoints are broken down by range.
              </p>
            </div>
            <div v-else-if="loading" class="text-center pa-5">
              <v-progress-circular indeterminate color="primary" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import {
    correlationCoverageMessage,
    dailyRowsFromActivity,
    explorerHasFilters,
    filterDegradedMessage,
    filterScopeCaption,
    quotaPeriodBounds,
    quotaPeriodRows,
    thresholdColor,
  } from '../objects/CloudProApi'
  import PeriodSelection from '../components/common/PeriodSelection.vue'
  import ApiUsageActivity from '../components/common/metrics/ApiUsageActivity.js'
  import moment from 'moment'

  export default {
    name: 'ApiUsage',
    components: {
      PeriodSelection,
    },
    data() {
      return {
        loading: true,
        error: false,
        errorText: '',
        usage: null,
        entitled: false,
        period: null,
        periodKey: '1hr',
        periodStart: null,
        periodEnd: null,
        selectedTokens: [],
        selectedEndpoints: [],
        showSummed: false,
        activity: null,
        chartLoading: false,
        chartError: false,
        chartErrorText: '',
        fetchRequestId: 0,
        section: 'graph',
        tableHeaders: [],
        tableData: [],
        pointStyles: ['circle', 'triangle', 'rect', 'rectRot'],
        tokenHeaders: [
          { text: 'Prefix', value: 'token_prefix' },
          { text: 'Username', value: 'username' },
          { text: 'Email', value: 'email' },
          { text: 'Comment', value: 'comment' },
          { text: 'Calls', value: 'used' },
        ],
        quotaPeriodHeaders: [
          { text: 'Explorer segment', value: 'segment' },
          { text: 'Quota period', value: 'quotaPeriod' },
          { text: 'Calls', value: 'calls' },
          { text: 'Tile scope', value: 'scope' },
        ],
      }
    },
    created() {
      const query = this.$route.query || {}
      this.selectedTokens = this.queryArray(query.tokens)
      this.selectedEndpoints = this.queryArray(query.endpoints)
      this.periodKey = query.period_key || '1hr'
      this.periodStart = query.start || null
      this.periodEnd = query.end || null
      this.section = query.section || 'graph'
      this.showSummed = query.show_summed === '1' || query.show_summed === 'true'
    },
    computed: {
      barColor() {
        return thresholdColor(this.usage && this.usage.threshold_status)
      },
      rangeSuffix() {
        if (!this.period) {
          return ''
        }
        const start = moment(this.period.start)
        const end = moment(this.period.end)
        const format = 'MMM D, HH:mm'
        const endText = start.isSame(end, 'day') ? end.format('HH:mm') : end.format(format)
        return '(' + start.format(format) + ' – ' + endText + ')'
      },
      dailyInRange() {
        return dailyRowsFromActivity(this.activity)
      },
      explorerFiltered() {
        return explorerHasFilters(this.selectedTokens, this.selectedEndpoints)
      },
      filterCaption() {
        if (!this.activity) {
          return ''
        }
        return filterScopeCaption(
          this.activity.filterActive,
          this.selectedTokens,
          this.selectedEndpoints,
          this.showSummed,
        )
      },
      chartColorBySelection() {
        if (this.showSummed) {
          return false
        }
        const visible = (this.activity && this.activity.visibleSeries()) || []
        return visible.length > 1
      },
      selectionTruncated() {
        return !!(this.activity && this.activity.selectionTruncated)
      },
      correlationWarning() {
        return correlationCoverageMessage(
          this.activity && this.activity.correlationCoverage,
        )
      },
      filterDegradedWarning() {
        return filterDegradedMessage(
          this.activity && {
            degraded: this.activity.filterDegraded,
          },
        )
      },
      tokensInRange() {
        return (this.activity && this.activity.tokenTotals) || []
      },
      endpointsInRange() {
        return (this.activity && this.activity.endpointTotals) || []
      },
      tokenChoices() {
        return (this.usage && this.usage.tokens || []).map((row) => ({
          value: row.token_guid || row.key,
          text: [row.token_prefix, row.comment, row.username, row.email]
            .filter(Boolean).join(' — ') || 'Unlabeled token',
        }))
      },
      endpointChoices() {
        const catalog = (this.usage && this.usage.endpoints || []).map((row) => row.key)
        const scoped = (this.activity && this.activity.endpointTotals || [])
          .map((row) => row.key)
          .filter(Boolean)
        if (this.selectedTokens.length && scoped.length) {
          return [...new Set([...scoped, ...catalog])].sort()
        }
        return catalog
      },
      currentQuotaPeriodBounds() {
        return quotaPeriodBounds(this.usage)
      },
      quotaPeriodRows() {
        const periods = (this.activity && this.activity.quotaPeriods) || []
        return quotaPeriodRows(
          periods,
          this.usage && this.usage.period_key,
          this.formatUsageTime,
        )
      },
    },
    watch: {
      period() {
        this.updateQuery()
      },
      selectedTokens() {
        this.updateQuery()
      },
      selectedEndpoints() {
        this.updateQuery()
      },
      showSummed() {
        this.updateQuery()
      },
      section(current) {
        this.updateQuery(false)
        if (current === 'data') {
          this.buildTable()
        }
      },
    },
    mounted() {
      this.axios.get('/usage').then((response) => {
        this.usage = response.data
        this.entitled = !!response.data.entitled
        this.loading = false
        this.fetchActivity()
      }).catch((error) => {
        this.error = true
        this.errorText = this.$helpers.parseError(error)
        this.loading = false
      })
    },
    beforeDestroy() {
      this.fetchRequestId += 1
    },
    methods: {
      formatUsageTime(value) {
        return value ? moment(value).format('MMM D, YYYY HH:mm Z') : 'unknown'
      },
      queryArray(value) {
        if (!value) {
          return []
        }
        return Array.isArray(value) ? value : [value]
      },
      updateQuery(fetch = true) {
        if (!this.period) {
          return
        }
        const query = {
          period_key: this.period.key,
          section: this.section,
        }
        if (this.period.key === 'custom') {
          // Presets are relative to now; a custom window has to carry its
          // bounds or the link cannot be shared or reloaded. A window ending
          // at "now" carries the sentinel, so reopening the link stays live
          // rather than freezing on whenever it was copied.
          query.start = this.period.start
          query.end = this.period.endsNow ? 'now' : this.period.end
        }
        if (this.selectedTokens.length) {
          query.tokens = this.selectedTokens
        }
        if (this.selectedEndpoints.length) {
          query.endpoints = this.selectedEndpoints
        }
        if (this.showSummed) {
          query.show_summed = '1'
        }
        this.$router.replace({ path: '/api-usage', query }).catch(() => {})
        if (fetch) {
          this.fetchActivity()
        }
      },
      fetchActivity() {
        if (!this.entitled || !this.period) {
          return
        }
        const requestId = ++this.fetchRequestId
        const activity = new ApiUsageActivity()
        this.chartLoading = true
        this.chartError = false
        this.chartErrorText = ''
        activity.start_time = this.period.start
        activity.end_time = this.period.end
        activity.interval = this.period.interval
        activity.tokens = this.selectedTokens.slice(0, 5)
        activity.endpoints = this.selectedEndpoints.slice(0, 5)
        activity.showSummed = this.showSummed
        activity.fetch().then(() => {
          if (requestId !== this.fetchRequestId) {
            return
          }
          this.activity = activity
          this.chartLoading = false
          if (this.section === 'data') {
            this.buildTable()
          }
        }).catch((error) => {
          if (requestId !== this.fetchRequestId) {
            return
          }
          this.chartError = true
          this.chartErrorText = this.$helpers.parseError(error)
          this.chartLoading = false
        })
      },
      buildTable() {
        if (!this.activity) {
          return
        }
        const tableData = {}
        const tableHeaders = [{
          text: 'Timestamp',
          align: 'start text-no-wrap',
          value: 'timestamp',
        }]
        this.activity.chartData().forEach((metric) => {
          tableHeaders.push({
            text: metric.label,
            value: metric.label,
            align: 'center',
          })
          metric.data.forEach((point) => {
            if (!point) {
              return
            }
            if (!(point.x in tableData)) {
              tableData[point.x] = {}
            }
            tableData[point.x][metric.label] = point.y
          })
        })
        this.tableHeaders = tableHeaders
        this.tableData = Object.keys(tableData).map((key) => ({
          timestamp: moment(key).toISOString(),
          ...tableData[key],
        }))
      },
      exportAsCsv() {
        this.buildTable()
        const headers = this.tableHeaders.map((header) => '"' + header.text + '"').join(',') + '\n'
        const body = this.tableData.map((row) => (
          this.tableHeaders.map((header) => {
            const wrapper = header.text === 'Timestamp' ? '"' : ''
            return row[header.value] == null ? 'null' : wrapper + row[header.value] + wrapper
          }).join(',')
        )).join('\n')
        const file = new Blob([headers + body], { type: 'text/plain' })
        this.$helpers.downloadFile('api-usage.csv', file)
      },
    },
  }
</script>
